import { NotificationCard } from '../../components/Card';
import {
  Button,
  Center,
  Flex,
  Icon,
  Box,
  Collapse,
  useDisclosure,
} from '@chakra-ui/react';
import { useMarkAsReadMutation } from '@/store/query/notification';
import { NotificationTime } from '../../components/Time';
import { ChevronDown, ChevronRight, LayersThree02 } from '@kula-ai/icons-core';
import { decode } from 'js-base64';
import { KText } from '@/ui-library/components';

/**
 * Parse custom_attributes from notification
 */
const useDataExportNotificationData = (notification) => {
  try {
    const { custom_attributes } = notification;
    if (!custom_attributes) return {};

    const data = JSON.parse(decode(custom_attributes));
    return {
      dateRange: data?.date_range || '',
      moduleLabels: data?.module_labels || '',
      csvExportUrl: data?.csv_export_url || '',
      recordsCount: data?.records_count,
      exportType: data?.export_type,
      isRecurring: data?.is_recurring,
      dataExportId: data?.data_export_id,
    };
  } catch (error) {
    console.error('Error parsing data export notification:', error);
    return {};
  }
};

const DataExportContent = ({ notification = {}, onClose = () => {} }) => {
  const [markAsRead] = useMarkAsReadMutation();
  const { isOpen, onToggle } = useDisclosure();
  const { dateRange, moduleLabels, csvExportUrl } =
    useDataExportNotificationData(notification);

  const handleDownload = () => {
    if (csvExportUrl) {
      markAsRead({ id: notification?.id });

      // Extract export ID from URL and navigate to local route
      try {
        const url = new URL(csvExportUrl);
        const exportId = url.pathname.split('/').pop();
        window.location.href = `/csv-exports/${exportId}`;
      } catch (error) {
        console.error('Error parsing CSV export URL:', error);
        // Fallback to direct navigation if URL parsing fails
        window.open(csvExportUrl, '_self');
      }
    }
  };

  return (
    <Flex alignItems="flex-start" gap="12px">
      <Center minW="34px" h="34px" borderRadius="6px" bg="primary-100">
        <LayersThree02
          size={16}
          color="var(--kula-colors-primary-500)"
          className="kula-icon"
        />
      </Center>

      <Flex flexDir="column" gap="8px" flex="1">
        {/* Title */}
        <KText size="sm-bold" color="secondary-900">
          Kula data export for {dateRange || 'selected period'} is ready for
          download
        </KText>

        {/* Collapsible Details Section */}
        <Box
          borderRadius="xs"
          border="1px solid"
          borderColor="border-color"
          overflow="hidden"
          w="100%"
          minW="0"
        >
          <Flex
            onClick={onToggle}
            flex="1"
            p="12px 16px"
            cursor="pointer"
            bg="secondary-25"
            gap="6px"
            alignItems="center"
            borderBottom={isOpen ? '1px solid' : 'none'}
            borderColor="border-color"
          >
            <Icon
              width="16px"
              height="16px"
              cursor="pointer"
              color="secondary-900"
              as={isOpen ? ChevronDown : ChevronRight}
            />
            <KText variant="heading_4" color="primary-900">
              Details
            </KText>
          </Flex>
          <Collapse
            in={isOpen}
            unmountOnExit={false}
            style={{ width: '100%', minWidth: 0 }}
          >
            <Flex flexDir="column" gap="16px" p="16px" bg="white" width="100%">
              {moduleLabels && (
                <Flex flexDir="row" gap="8px" minW="0" overflow="hidden">
                  <KText
                    size="sm-semi-bold"
                    minW="fit-content"
                    flexShrink={0}
                    color="secondary-900"
                  >
                    Data points:
                  </KText>
                  <KText flex="1" minW="0" color="secondary-700">
                    {moduleLabels}
                  </KText>
                </Flex>
              )}

              {dateRange && (
                <Flex flexDir="row" gap="8px" minW="0" overflow="hidden">
                  <KText
                    size="sm-semi-bold"
                    minW="fit-content"
                    flexShrink={0}
                    color="secondary-900"
                  >
                    Date range:
                  </KText>
                  <KText flex="1" minW="0" color="secondary-700">
                    {dateRange}
                  </KText>
                </Flex>
              )}
            </Flex>
          </Collapse>
        </Box>

        {/* Timestamp */}
        <NotificationTime notification={notification} />

        {/* Download Button */}
        {csvExportUrl && (
          <Button
            w="fit-content"
            variant="primary"
            borderRadius="8px"
            onClick={handleDownload}
            size="sm"
            h="32px"
            px="16px"
            data-test-id="download-data-export-button"
          >
            Download
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

export const DataExportNotification = ({
  notification = {},
  onClose = () => {},
}) => {
  return (
    <NotificationCard notification={notification}>
      <DataExportContent notification={notification} onClose={onClose} />
    </NotificationCard>
  );
};

export default DataExportNotification;
